/**
 * Active dataset for the current epi-chat session.
 *
 * Surfaces the dataset release id that downstream chat surfaces should
 * treat as "pinned" — currently used by the
 * ``concept_group_prevalence_preview`` panel inside
 * ``ConceptGroupDetailsDialog`` to know which dataset to query.
 *
 * Semantics:
 *   - `null` (default) → no pinned dataset; the consumer renders a
 *     "pin a dataset" placeholder (or no-ops, depending on the surface).
 *   - ``ActiveDataset`` → ``id`` (fully-qualified ``DATABASE.SCHEMA``
 *     handle, same shape the cohort runner uses) and an optional
 *     ``displayName`` (the curated catalog RELEASE_NAME) for UI rendering.
 *
 * Selection rule (see ``selectActiveDataset``): the most recently
 * updated artifact's latest execution. Both ``datasetReleaseId`` and
 * ``datasetReleaseDisplayName`` come from that same execution, so the
 * pair is always consistent. Falls back through the artifact list in
 * reverse-update order so a fresh analytical variable can override an older one without
 * the user having to re-pin anything.
 *
 * The provider is mounted unconditionally — gating happens at the
 * consumer (the dialog), so when the FF is OFF the value is harmless
 * dead code. We deliberately avoid FF-gating the provider itself so a
 * later non-FF consumer doesn't need a second copy of this plumbing.
 */

// TODO: cleanup with concept_group_prevalence_preview ff — once the FF
// is removed this provider stays; only the consumer changes.

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { EpiArtifact } from '../types/epi-chat-types';

export interface ActiveDataset {
  id: string;
  displayName?: string;
}

type ActiveDatasetValue = ActiveDataset | null;

const ActiveDatasetContext = createContext<ActiveDatasetValue>(null);

interface ActiveDatasetProviderProps {
  dataset: ActiveDatasetValue;
  children: ReactNode;
}

export const ActiveDatasetProvider = ({ dataset, children }: ActiveDatasetProviderProps) => (
  <ActiveDatasetContext.Provider value={dataset}>{children}</ActiveDatasetContext.Provider>
);

export const useActiveDataset = (): ActiveDatasetValue => useContext(ActiveDatasetContext);

const datasetForArtifact = (artifact: EpiArtifact): ActiveDataset | null => {
  const exec = artifact.executions?.[0];
  if (!exec?.datasetReleaseId) return null;
  return {
    id: exec.datasetReleaseId,
    displayName: exec.datasetReleaseDisplayName,
  };
};

/**
 * Pick the dataset to surface as "active" from a session's artifacts.
 *
 * Sort order: newest ``updatedAt`` first, with ties broken by array
 * position. Artifacts without any execution are skipped so a freshly
 * minted markdown summary doesn't blank out the dataset that the
 * preceding SQL artifact stamped via its run. Returns ``null`` when no
 * artifact carries an execution — typical of pre-finalize sessions
 * where the agent is still gathering intent.
 */
export const selectActiveDataset = (artifacts: readonly EpiArtifact[]): ActiveDatasetValue => {
  let best: EpiArtifact | null = null;
  for (const artifact of artifacts) {
    if (!datasetForArtifact(artifact)) continue;
    if (best === null || artifact.updatedAt > best.updatedAt) {
      best = artifact;
    }
  }
  return best ? datasetForArtifact(best) : null;
};

/**
 * Convenience hook for surfaces that already have access to the
 * artifact list and want to compute the active dataset without
 * threading a provider through every level. Pure projection of
 * ``selectActiveDataset`` memoised on artifact identity.
 */
export const useDeriveActiveDataset = (artifacts: readonly EpiArtifact[]): ActiveDatasetValue =>
  useMemo(() => selectActiveDataset(artifacts), [artifacts]);
